// ─────────────────────────────────────────────────────────────────────
// src/features/system/developer/components/video-gallery/states.ts
//
// FE mirror of `bff/modules/services/video-pipeline/states.mjs`.
//
// Hand-mirrored (no codegen) because the BFF side is .mjs and there are
// only six values — generation would add more weight than the duplication.
// CI guard: see `test-bff-video-states-parity.test.mjs` which fails if
// the two files drift out of sync.
//
// Reference UX patterns: YouTube Studio's per-stage upload toast,
// TikTok's "Processing video…" overlay, Instagram Reels' multi-step
// progress chip. All three derive a 0–100 % bar from a server-driven
// status string exactly like this module.
// ─────────────────────────────────────────────────────────────────────

export type VideoStatus =
  | 'initiated'
  | 'uploaded'
  | 'ingested'
  | 'extracted'
  | 'transcribed'
  | 'sidecars_written'
  | 'failed';

/** Ordered happy-path stages. Index drives the progress bar. */
export const PIPELINE_STAGES: readonly VideoStatus[] = [
  'initiated',
  'uploaded',
  'ingested',
  'extracted',
  'transcribed',
  'sidecars_written',
] as const;

export const TERMINAL_STATUSES = new Set<VideoStatus>(['sidecars_written', 'failed']);
export const IN_FLIGHT_STATUSES = new Set<VideoStatus>([
  'uploaded',
  'ingested',
  'extracted',
  'transcribed',
]);

export function isTerminal(status: string): boolean {
  return TERMINAL_STATUSES.has(status as VideoStatus);
}

export function isInFlight(status: string): boolean {
  return IN_FLIGHT_STATUSES.has(status as VideoStatus);
}

export function isFailed(status: string): boolean {
  return status === 'failed';
}

// ─── Cold-start "warming up" window ──────────────────────────────────────
//
// After the worker-agile scale-to-zero PUSH migration, the faststart/DAG
// worker idles at min_instances=0. A freshly-dispatched job (entity at
// `uploaded`, bytes landed + Pub/Sub published) may wait a few seconds while
// Pub/Sub PUSH wakes the cold Cloud Run instance. For that window we surface
// a distinct "Warming up the processor…" state so the gap reads as healthy
// cold-start latency — NOT a stall. This is a pure presentation derivation
// (no new lifecycle/entity status): it refines the `uploaded` stage based on
// the row's age. Past the window the label falls back to the plain stage
// label (the row is genuinely queued behind other work or awaiting the
// reconciler), so a stuck row never reads "warming" forever.

/** How long after dispatch an `uploaded` row reads as cold-start "warming". */
export const WARMING_UP_WINDOW_MS = 20_000;

/** Operator-facing copy for the cold-start window. */
export const WARMING_UP_LABEL = 'Warming up the processor…';

/**
 * True when the row is in the post-dispatch cold-start window: status is
 * `uploaded` (dispatched, worker not yet ingested) AND it was created within
 * `WARMING_UP_WINDOW_MS`. `ageMs` is `now - created_at_ms`; pass a negative
 * value (unknown age) to opt out.
 */
export function isWarmingUp(status: string, ageMs: number): boolean {
  return status === 'uploaded' && ageMs >= 0 && ageMs < WARMING_UP_WINDOW_MS;
}

/**
 * Progress 0..1 from current status. `failed` returns 0 so UI surfaces
 * can render it as an error chip rather than a half-filled bar.
 * Unknown statuses also map to 0.
 *
 * Mirror of states.mjs `progressFraction()`.
 */
export function progressFraction(status: string): number {
  if (status === 'failed') return 0;
  const idx = PIPELINE_STAGES.indexOf(status as VideoStatus);
  if (idx < 0) return 0;
  const last = PIPELINE_STAGES.length - 1;
  return last <= 0 ? 1 : idx / last;
}

/** 0–100 integer for display. */
export function progressPercent(status: string): number {
  return Math.round(progressFraction(status) * 100);
}

/**
 * Short human-friendly label per stage. Used by the progress bar caption
 * and the preview-modal step indicator. Localisation-ready — wire to
 * i18n when the locale layer lands.
 */
export const STAGE_LABEL: Record<VideoStatus, string> = {
  initiated:        'Uploading',
  uploaded:         'Queued',
  ingested:         'Ingested',
  extracted:        'Extracting audio',
  transcribed:      'Transcribing',
  sidecars_written: 'Ready',
  failed:           'Failed',
};

export function stageLabel(status: string): string {
  return STAGE_LABEL[status as VideoStatus] ?? 'Unknown';
}

/**
 * Composite UI descriptor — single call site for any component that
 * needs to render pipeline state. Avoids each component re-deriving
 * the same five booleans + label + percent.
 */
export interface PipelineProgress {
  status: string;
  label: string;
  percent: number;      // 0..100 integer
  fraction: number;     // 0..1
  isTerminal: boolean;
  isInFlight: boolean;
  isFailed: boolean;
  isComplete: boolean;  // happy-path terminal
  isWarming: boolean;   // post-dispatch cold-start window
}

export function pipelineProgress(status: string, ageMs?: number): PipelineProgress {
  const fraction = progressFraction(status);
  const failed = isFailed(status);
  const terminal = isTerminal(status);
  const warming = typeof ageMs === 'number' && isWarmingUp(status, ageMs);
  return {
    status,
    label: warming ? WARMING_UP_LABEL : stageLabel(status),
    percent: Math.round(fraction * 100),
    fraction,
    isTerminal: terminal,
    isInFlight: isInFlight(status),
    isFailed: failed,
    isComplete: terminal && !failed,
    isWarming: warming,
  };
}
